import React, { useMemo, useRef } from "react";
import { Box, BoxProps, Paper, PopoverProps } from "@mui/material";
import { uploadMultiPartFileApi } from "@/features/createProject/api";
import uppyFactory from "@/features/createProject/uppyFactory";
import { useAppDispatch, useTypedSelector } from "@/config/configureAppStore";
import {
  removeFileFromPopUp,
  removeUploadedSampleFileId,
  updateSourceFileResponseList
} from "@/reducers/slices/clientProjectPopupSlice/clientProjectPopupSlice";
import useUppy from "@/features/createProject/uppyFactory/hooks/useUppy";
import { SingleFormatFileList } from ".";
import { FILE_TYPES, SUPPORTED_FORMATS } from "@/features/clientProjectDetails";
import {
  UploadedOrUppyFile,
  filterFiles,
  getCount
} from "@/features/createProject";
import { MEDIA_TYPE_EXTN } from "@/features/clientSearch/constant";
import { useParams } from "react-router-dom";
import { removeFile } from "@/reducers/slices/currentClientProjectSlice/currentClientProjectSlice";
import { VisuallyHiddenFileInput } from "@/components/elements/VisuallyHiddenFileInput";
import { removeFileFromUppy } from "@/reducers/slices/uppySlice/uppySlice";

export interface ListFilesStatusProps {
  anchorEl?: any;
  showStatusOfSelectedFile: boolean;
  wrapperProps?: BoxProps;
  popoverProps?: Partial<PopoverProps>;
}

export const ListFilesStatus = ({
  anchorEl,
  showStatusOfSelectedFile = false,
  wrapperProps
}: ListFilesStatusProps): React.ReactNode => {
  const dispatch = useAppDispatch();

  const activeWorkspaceId = useTypedSelector(
    (state) => state.user.activeWorkspaceId
  );

  const { uploadedFiles, filesData: _filesData } = useUppy();
  const projectIdFromParams = useParams().projectId;
  const projectId =
    useTypedSelector((state) => state.clientProjectPopup.projectId) ||
    projectIdFromParams;
  const replaceWithFileId = useRef<string>();
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const filesData = useMemo(() => {
    return { ..._filesData };
  }, [_filesData]);

  const handleRemoveFileForUpload = (fileKey: string) => {
    if (projectId && fileKey && uppyFactory.abortFileUploadByUser) {
      uppyFactory.abortFileUploadByUser(fileKey);
      setTimeout(() => {
        uploadMultiPartFileApi.removeFile({ fileId: fileKey }).then(() => {
          dispatch(
            removeFileFromUppy({
              projectId,
              sourceFileId: fileKey
            })
          );
          // in case of polling, remove the file from the list
          dispatch(
            removeFile({
              fileId: fileKey,
              projectId
            })
          );
        });
      }, 100);
    }
  };

  const handleFileRetry = (fileKey: string) => {
    if (fileKey) {
      const isDone = uppyFactory.retryUpload(fileKey);
      if (!isDone) {
        replaceWithFileId.current = fileKey;
        if (fileInputRef.current) {
          fileInputRef.current.click();
        }
      }
    }
  };
  const handleUnlinkedFileUploads = async (fileKey: string) => {
    console.log("handleUnlinkedFileUploads", fileKey);
    if (fileKey) {
      uploadMultiPartFileApi
        .abortParts(fileKey, activeWorkspaceId!)
        .finally(() => {
          if (!fileKey.startsWith("SFSAMF")) {
            dispatch(
              updateSourceFileResponseList({
                sourceFileId: fileKey,
                fileStatus: "FAILED"
              })
            );
          }
          uploadMultiPartFileApi.removeFile({ fileId: fileKey }).then(() => {
            if (projectId) {
              dispatch(
                removeFile({
                  fileId: fileKey!,
                  projectId: projectId
                })
              );
              dispatch(
                removeFileFromPopUp({
                  fileId: fileKey!,
                  projectId: projectId
                })
              );
              if (uploadedFiles?.[fileKey]?.sourceSampleFileId) {
                dispatch(
                  removeUploadedSampleFileId(
                    uploadedFiles?.[fileKey]?.sourceSampleFileId as string
                  )
                );
              }
            }
          });
        });
    }
  };

  const audioFiles = useMemo(
    () =>
      filterFiles({
        data: filesData as UploadedOrUppyFile,
        fileTypeString: MEDIA_TYPE_EXTN.AUDIO
      }),
    [filesData]
  );

  const uploadedAudioFiles = useMemo(
    () =>
      filterFiles({
        data: uploadedFiles as UploadedOrUppyFile,
        fileTypeEnum: FILE_TYPES.BG_AUDIO,
        fileTypeString: MEDIA_TYPE_EXTN.AUDIO,
        excludeFiles: filesData
      }),
    [uploadedFiles, filesData]
  );

  const videoFiles = useMemo(
    () =>
      filterFiles({
        data: filesData as UploadedOrUppyFile,
        fileTypeString: MEDIA_TYPE_EXTN.VIDEO
      }),
    [filesData]
  );

  const uploadedVideoFiles = useMemo(
    () =>
      filterFiles({
        data: uploadedFiles as UploadedOrUppyFile,
        fileTypeEnum: FILE_TYPES.VIDEO,
        fileTypeString: MEDIA_TYPE_EXTN.VIDEO,
        excludeFiles: filesData
      }),
    [uploadedFiles, filesData]
  );

  const audioCount: number = useMemo(
    () =>
      getCount({
        files: audioFiles,
        uploadedFiles: uploadedAudioFiles,
        showStatusOfSelectedFile
      }),
    [audioFiles, uploadedAudioFiles, showStatusOfSelectedFile]
  );

  const videoCount: number = useMemo(
    () =>
      getCount({
        files: videoFiles,
        uploadedFiles: uploadedVideoFiles,
        showStatusOfSelectedFile
      }),
    [videoFiles, uploadedVideoFiles, showStatusOfSelectedFile]
  );

  if (!audioCount && !videoCount) {
    return null;
  }

  return (
    <>
      <Box
        width={anchorEl?.current ? anchorEl.current?.clientWidth : "100%"}
        minWidth={400}
        borderRadius={2}
        component={Paper}
        elevation={0}
        maxHeight={400}
        overflow={"auto"}
        {...wrapperProps}
      >
        {videoCount ? (
          <SingleFormatFileList
            {...{
              currentFiles: videoFiles,
              fileCount: videoCount,
              fileTitle: "Videos",
              handleFileRetry,
              handleRemoveFileForUpload,
              handleUnlinkedFileUploads,
              uploadedFiles: showStatusOfSelectedFile
                ? undefined
                : uploadedVideoFiles
            }}
          />
        ) : null}
        {audioCount ? (
          <SingleFormatFileList
            {...{
              currentFiles: audioFiles,
              fileCount: audioCount,
              fileTitle: "Audio",
              handleFileRetry,
              handleRemoveFileForUpload,
              handleUnlinkedFileUploads,
              uploadedFiles: showStatusOfSelectedFile
                ? undefined
                : uploadedAudioFiles
            }}
          />
        ) : null}
      </Box>
      <VisuallyHiddenFileInput
        inputRef={fileInputRef}
        max={1}
        acceptedFormats={SUPPORTED_FORMATS}
        onChange={(e) => {
          const _replaceWithFileId = replaceWithFileId.current;
          if (
            e?.target?.files?.[0] &&
            _replaceWithFileId?.length &&
            projectId
          ) {
            uppyFactory.setProjectId(projectId);
            uppyFactory.uploadFile(e.target.files[0], _replaceWithFileId);
            setTimeout(() => {
              dispatch(
                removeFile({
                  fileId: _replaceWithFileId,
                  projectId
                })
              );
              dispatch(
                removeFileFromPopUp({
                  fileId: _replaceWithFileId,
                  projectId
                })
              );
            }, 100);
          }
        }}
      />
    </>
  );
};
